import { useCallback, useEffect, useState } from 'react';
import { useConfirmDialog } from '../hooks/useConfirmDialog';
import { Link, useSearchParams } from 'react-router-dom';
import { usePlatformAccessLevel } from '../features/platformPermissions/usePlatformAccessLevel';
import { DrillFormModal } from '../features/emergency/DrillFormModal';
import { EmergencyAttachmentsModal } from '../features/emergency/EmergencyAttachmentsModal';
import { PlanFormModal } from '../features/emergency/PlanFormModal';
import {
  createEmergencyDrill,
  createEmergencyPlan,
  escalateDrillFindings,
  fetchEmergencyDrills,
  fetchEmergencyPlans,
  fetchUpcomingDrills,
  updateEmergencyDrill,
  updateEmergencyPlan,
} from '../features/emergency/emergencyApi';
import type {
  EmergencyDrillSummary,
  EmergencyPlanSummary,
  UpcomingDrillPlan,
} from '../features/emergency/types';
import {
  DRILL_RESULT_LABEL,
  PLAN_STATUS_LABEL,
  SCENARIO_LABEL,
  drillResultBadgeClass,
  planStatusBadgeClass,
  scenarioBadgeClass,
  upcomingStateBadgeClass,
  upcomingStateLabel,
} from '../features/emergency/emergencyUtils';

type TabId = 'plans' | 'drills' | 'upcoming';

type AttachTarget =
  | { kind: 'emergency_plan'; id: string; label: string }
  | { kind: 'emergency_drill'; id: string; label: string };

function formatDate(value?: string | null): string {
  if (!value) return '—';
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return value;
  return d.toLocaleDateString('es-AR');
}

export function EmergencyPage() {
  const { confirm } = useConfirmDialog();
  const { canEdit } = usePlatformAccessLevel('emergency.access');
  const [searchParams] = useSearchParams();
  const tabParam = searchParams.get('tab');
  const initialTab: TabId =
    tabParam === 'drills' || tabParam === 'upcoming' || tabParam === 'plans' ? tabParam : 'plans';
  const [tab, setTab] = useState<TabId>(initialTab);
  const [plans, setPlans] = useState<EmergencyPlanSummary[]>([]);
  const [drills, setDrills] = useState<EmergencyDrillSummary[]>([]);
  const [upcoming, setUpcoming] = useState<UpcomingDrillPlan[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [planPage, setPlanPage] = useState(1);
  const [drillPage, setDrillPage] = useState(1);
  const [planPages, setPlanPages] = useState(1);
  const [drillPages, setDrillPages] = useState(1);
  const [planCreateOpen, setPlanCreateOpen] = useState(false);
  const [editPlan, setEditPlan] = useState<EmergencyPlanSummary | null>(null);
  const [drillCreateOpen, setDrillCreateOpen] = useState(false);
  const [editDrill, setEditDrill] = useState<EmergencyDrillSummary | null>(null);
  const [attachTarget, setAttachTarget] = useState<AttachTarget | null>(null);

  const activePlans = plans.filter((p) => p.status === 'activo');

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [plansRes, drillsRes, upcomingRes] = await Promise.all([
        fetchEmergencyPlans({ page: planPage, pageSize: 20 }),
        fetchEmergencyDrills({ page: drillPage, pageSize: 20 }),
        fetchUpcomingDrills(),
      ]);
      setPlans(plansRes.data);
      setPlanPages(plansRes.pagination.pages);
      setDrills(drillsRes.data);
      setDrillPages(drillsRes.pagination.pages);
      setUpcoming(upcomingRes);
    } catch {
      setError('No se pudieron cargar los datos de emergencias.');
    } finally {
      setLoading(false);
    }
  }, [planPage, drillPage]);

  useEffect(() => {
    void load();
  }, [load]);

  useEffect(() => {
    if (tabParam === 'drills' || tabParam === 'upcoming' || tabParam === 'plans') {
      setTab(tabParam);
    }
  }, [tabParam]);

  const handleEscalate = async (drill: EmergencyDrillSummary) => {
    const ok = await confirm({
      title: 'Escalar a CAPA',
      message: '¿Escalar hallazgos de este simulacro a CAPA?',
      confirmText: 'Escalar',
      variant: 'danger',
    });
    if (!ok) return;
    await escalateDrillFindings(drill.id);
    await load();
  };

  const tabClass = (id: TabId) =>
    `px-4 py-2 text-sm font-medium border-b-2 -mb-px ${
      tab === id
        ? 'border-primary text-primary'
        : 'border-transparent text-muted-foreground hover:text-foreground'
    }`;

  return (
    <div className="p-6 space-y-6">
      <div className="flex flex-wrap items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold">Emergencias — planes y simulacros</h1>
          <p className="text-muted-foreground text-sm">ISO 14001 / 45001 8.2 — preparación y respuesta</p>
        </div>
        {canEdit && (
          <div className="flex gap-2">
            {tab === 'plans' && (
              <button type="button" className="btn btn-primary" onClick={() => setPlanCreateOpen(true)}>
                Nuevo plan
              </button>
            )}
            {tab === 'drills' && (
              <button
                type="button"
                className="btn btn-primary"
                onClick={() => setDrillCreateOpen(true)}
                disabled={activePlans.length === 0}
              >
                Registrar simulacro
              </button>
            )}
          </div>
        )}
      </div>

      {error && <p className="text-sm text-destructive">{error}</p>}

      <div className="flex gap-1 border-b border-border">
        <button type="button" className={tabClass('plans')} onClick={() => setTab('plans')}>
          Planes
        </button>
        <button type="button" className={tabClass('drills')} onClick={() => setTab('drills')}>
          Bitácora de simulacros
        </button>
        <button type="button" className={tabClass('upcoming')} onClick={() => setTab('upcoming')}>
          Próximos simulacros
          {upcoming.length > 0 && (
            <span className="ml-2 inline-flex items-center justify-center rounded-full bg-amber-100 text-amber-800 text-xs px-2 py-0.5">
              {upcoming.length}
            </span>
          )}
        </button>
      </div>

      {tab === 'plans' && (
        <div className="rounded-lg border border-border overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-muted/50">
              <tr>
                <th className="text-left p-3 font-medium">Plan</th>
                <th className="text-left p-3 font-medium">Escenario</th>
                <th className="text-left p-3 font-medium">Último simulacro</th>
                <th className="text-left p-3 font-medium">Próximo simulacro</th>
                <th className="text-left p-3 font-medium">Estado</th>
                <th className="text-left p-3 font-medium">Acciones</th>
              </tr>
            </thead>
            <tbody>
              {loading && (
                <tr>
                  <td colSpan={6} className="p-6 text-center text-muted-foreground">
                    Cargando…
                  </td>
                </tr>
              )}
              {!loading && plans.length === 0 && (
                <tr>
                  <td colSpan={6} className="p-6 text-center text-muted-foreground">
                    No hay planes de emergencia registrados.
                  </td>
                </tr>
              )}
              {!loading &&
                plans.map((plan) => (
                  <tr key={plan.id} className="border-t border-border hover:bg-muted/30">
                    <td className="p-3">
                      <div className="font-medium">{plan.name}</div>
                      <div className="text-xs text-muted-foreground line-clamp-2">{plan.procedureNote}</div>
                    </td>
                    <td className="p-3">
                      <span
                        className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${scenarioBadgeClass(plan.scenario)}`}
                      >
                        {SCENARIO_LABEL[plan.scenario]}
                      </span>
                    </td>
                    <td className="p-3 whitespace-nowrap">{formatDate(plan.lastDrillAt)}</td>
                    <td className="p-3 whitespace-nowrap">{formatDate(plan.nextDrillAt)}</td>
                    <td className="p-3">
                      <span
                        className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${planStatusBadgeClass(plan.status)}`}
                      >
                        {PLAN_STATUS_LABEL[plan.status]}
                      </span>
                    </td>
                    <td className="p-3 space-x-2 whitespace-nowrap">
                      {canEdit && (
                        <button
                          type="button"
                          className="text-primary hover:underline text-xs"
                          onClick={() => setEditPlan(plan)}
                        >
                          Editar
                        </button>
                      )}
                      <button
                        type="button"
                        className="text-primary hover:underline text-xs"
                        onClick={() =>
                          setAttachTarget({
                            kind: 'emergency_plan',
                            id: plan.id,
                            label: plan.name,
                          })
                        }
                      >
                        Adjuntos
                      </button>
                    </td>
                  </tr>
                ))}
            </tbody>
          </table>
          {planPages > 1 && (
            <div className="flex items-center justify-center gap-3 p-4 border-t border-border">
              <button
                type="button"
                className="btn btn-outline btn-sm"
                disabled={planPage <= 1}
                onClick={() => setPlanPage((p) => p - 1)}
              >
                Anterior
              </button>
              <span className="text-sm text-muted-foreground">
                Página {planPage} de {planPages}
              </span>
              <button
                type="button"
                className="btn btn-outline btn-sm"
                disabled={planPage >= planPages}
                onClick={() => setPlanPage((p) => p + 1)}
              >
                Siguiente
              </button>
            </div>
          )}
        </div>
      )}

      {tab === 'drills' && (
        <div className="rounded-lg border border-border overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-muted/50">
              <tr>
                <th className="text-left p-3 font-medium">Fecha</th>
                <th className="text-left p-3 font-medium">Plan</th>
                <th className="text-left p-3 font-medium">Resultado</th>
                <th className="text-left p-3 font-medium">Hallazgos</th>
                <th className="text-left p-3 font-medium">CAPA</th>
                <th className="text-left p-3 font-medium">Acciones</th>
              </tr>
            </thead>
            <tbody>
              {loading && (
                <tr>
                  <td colSpan={6} className="p-6 text-center text-muted-foreground">
                    Cargando…
                  </td>
                </tr>
              )}
              {!loading && drills.length === 0 && (
                <tr>
                  <td colSpan={6} className="p-6 text-center text-muted-foreground">
                    No hay simulacros registrados.
                  </td>
                </tr>
              )}
              {!loading &&
                drills.map((drill) => (
                  <tr key={drill.id} className="border-t border-border hover:bg-muted/30">
                    <td className="p-3 whitespace-nowrap">{formatDate(drill.date)}</td>
                    <td className="p-3">
                      <div className="font-medium">{drill.plan?.name ?? '—'}</div>
                      {drill.plan && (
                        <span className="text-xs text-muted-foreground">
                          {SCENARIO_LABEL[drill.plan.scenario]}
                        </span>
                      )}
                    </td>
                    <td className="p-3">
                      <span
                        className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${drillResultBadgeClass(drill.result)}`}
                      >
                        {DRILL_RESULT_LABEL[drill.result]}
                      </span>
                    </td>
                    <td className="p-3 text-xs text-muted-foreground line-clamp-2 max-w-xs">
                      {drill.findingsNote || '—'}
                    </td>
                    <td className="p-3">
                      {drill.bridgeRecordId ? (
                        <Link
                          to={`/registros/no-conformidades/${drill.bridgeRecordId}`}
                          className="text-primary hover:underline text-xs"
                        >
                          Ver NC
                        </Link>
                      ) : (
                        <span className="text-muted-foreground">—</span>
                      )}
                    </td>
                    <td className="p-3 space-x-2 whitespace-nowrap">
                      {canEdit && (
                        <button
                          type="button"
                          className="text-primary hover:underline text-xs"
                          onClick={() => setEditDrill(drill)}
                        >
                          Editar
                        </button>
                      )}
                      <button
                        type="button"
                        className="text-primary hover:underline text-xs"
                        onClick={() =>
                          setAttachTarget({
                            kind: 'emergency_drill',
                            id: drill.id,
                            label: drill.plan?.name ?? 'Simulacro',
                          })
                        }
                      >
                        Adjuntos
                      </button>
                      {canEdit && !drill.bridgeRecordId && drill.result !== 'satisfactorio' && (
                        <button
                          type="button"
                          className="text-primary hover:underline text-xs"
                          onClick={() => void handleEscalate(drill)}
                        >
                          → CAPA
                        </button>
                      )}
                    </td>
                  </tr>
                ))}
            </tbody>
          </table>
          {drillPages > 1 && (
            <div className="flex items-center justify-center gap-3 p-4 border-t border-border">
              <button
                type="button"
                className="btn btn-outline btn-sm"
                disabled={drillPage <= 1}
                onClick={() => setDrillPage((p) => p - 1)}
              >
                Anterior
              </button>
              <span className="text-sm text-muted-foreground">
                Página {drillPage} de {drillPages}
              </span>
              <button
                type="button"
                className="btn btn-outline btn-sm"
                disabled={drillPage >= drillPages}
                onClick={() => setDrillPage((p) => p + 1)}
              >
                Siguiente
              </button>
            </div>
          )}
        </div>
      )}

      {tab === 'upcoming' && (
        <div className="rounded-lg border border-border overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-muted/50">
              <tr>
                <th className="text-left p-3 font-medium">Plan</th>
                <th className="text-left p-3 font-medium">Escenario</th>
                <th className="text-left p-3 font-medium">Fecha programada</th>
                <th className="text-left p-3 font-medium">Alerta</th>
              </tr>
            </thead>
            <tbody>
              {loading && (
                <tr>
                  <td colSpan={4} className="p-6 text-center text-muted-foreground">
                    Cargando…
                  </td>
                </tr>
              )}
              {!loading && upcoming.length === 0 && (
                <tr>
                  <td colSpan={4} className="p-6 text-center text-muted-foreground">
                    No hay simulacros próximos o vencidos en los próximos 30 días.
                  </td>
                </tr>
              )}
              {!loading &&
                upcoming.map((plan) => (
                  <tr key={plan.id} className="border-t border-border hover:bg-muted/30">
                    <td className="p-3 font-medium">{plan.name}</td>
                    <td className="p-3">
                      <span
                        className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${scenarioBadgeClass(plan.scenario)}`}
                      >
                        {SCENARIO_LABEL[plan.scenario]}
                      </span>
                    </td>
                    <td className="p-3 whitespace-nowrap">{formatDate(plan.nextDrillAt)}</td>
                    <td className="p-3">
                      <span
                        className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${upcomingStateBadgeClass(plan.state)}`}
                      >
                        {upcomingStateLabel(plan.state, plan.daysUntilDue)}
                      </span>
                    </td>
                  </tr>
                ))}
            </tbody>
          </table>
        </div>
      )}

      <PlanFormModal
        open={planCreateOpen}
        onClose={() => setPlanCreateOpen(false)}
        title="Nuevo plan de emergencia"
        onSubmit={async (payload) => {
          await createEmergencyPlan(payload);
          await load();
        }}
      />

      <PlanFormModal
        open={editPlan !== null}
        onClose={() => setEditPlan(null)}
        title="Editar plan de emergencia"
        initial={editPlan ?? undefined}
        onSubmit={async (payload) => {
          if (!editPlan) return;
          await updateEmergencyPlan(editPlan.id, payload);
          await load();
        }}
      />

      <DrillFormModal
        open={drillCreateOpen}
        onClose={() => setDrillCreateOpen(false)}
        title="Registrar simulacro"
        plans={activePlans}
        onSubmit={async (payload) => {
          await createEmergencyDrill(payload);
          await load();
        }}
      />

      <DrillFormModal
        open={editDrill !== null}
        onClose={() => setEditDrill(null)}
        title="Editar simulacro"
        plans={activePlans.length > 0 ? activePlans : plans}
        initial={editDrill ?? undefined}
        onSubmit={async (payload) => {
          if (!editDrill) return;
          await updateEmergencyDrill(editDrill.id, payload);
          await load();
        }}
      />

      <EmergencyAttachmentsModal
        open={attachTarget !== null}
        onClose={() => setAttachTarget(null)}
        resourceType={attachTarget?.kind ?? 'emergency_plan'}
        resourceId={attachTarget?.id ?? ''}
        title={
          attachTarget
            ? `Adjuntos — ${attachTarget.label}`
            : 'Adjuntos'
        }
        readOnly={!canEdit}
      />
    </div>
  );
}
